import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, CircularProgress, TextField } from "@mui/material";
import MainPage from "../MainPage";

const STEPS = [
  {
    name: "intro",
    headline: "The Value of Real",
    copy: "New research on what makes public affairs creative feel trustworthy, authentic, and recognizably human as AI-generated content becomes more common.",
    insight: "AI & creative perception: national voter survey results",
    mapLabel: "Views on AI in advertising by congressional district",
    // Identical to the trust-drivers mapView (incl. the default 18-34 age cut)
    // so revealing slide 2 changes NOTHING on the map: the age-filtered data
    // loads once behind the blurred landing hero and simply un-blurs.
    mapView: { variable: "nji_ad_views", geoLevel: "cd", preferenceFilter: "top", viewMode: "top", topStrengthShading: false, age: 1 },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 0 },
  },
  {
    name: "trust-drivers",
    headline: "Authenticity is becoming more valuable, especially with younger audiences.",
    copy: "Accuracy remains the foundation of trust, but real stories, experiences, and human voices are becoming stronger differentiators. That shift is most pronounced among younger adults, who are also more likely to use AI tools and already place authenticity ahead of accuracy.",
    insight: "The shift is generational, not just demographic",
    mapLabel: "Views on AI advertising: winning statement by CD",
    mapView: { variable: "nji_ad_views", geoLevel: "cd", preferenceFilter: "top", viewMode: "top", topStrengthShading: false, age: 1 },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
    mapHoverHint: true,
  },
  {
    name: "messengers",
    headline: "People want AI to support humans, not replace them.",
    copy: "Real people win everywhere. The regional split is not human versus synthetic, but which kind of real voice audiences trust most.",
    insight: "Universality first, regional variation second",
    mapLabel: "Messenger preference: leading voice by CD",
    mapView: { variable: "nji_ad_authentic", geoLevel: "cd", preferenceFilter: "top", viewMode: "top", topStrengthShading: false },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
    mapHoverHint: true,
  },
  {
    name: "ai-reaction",
    headline: "Using AI is not the problem. Misleading people with it is.",
    copy: "The penalty is not AI itself, but discovering that a synthetic person was passed off as real. The harshest reaction is strongest in the districts brands can least afford to lose.",
    insight: "Feeling misled is what breaks trust",
    mapLabel: "AI reaction: combined negative response by CD (48–64%)",
    mapView: { variable: "nji_ad_reaction", geoLevel: "cd", preferenceFilter: "negsum", viewMode: "heat", heatDomain: [0.48, 0.64] },
    stateZoom: "CA",
    flyTo: { type: "fitBounds", bounds: [[-124.6, 32.4], [-113.9, 42.1]], duration: 1200 },
    mapHoverHint: true,
  },
  {
    name: "bipartisan",
    headline: "What earns trust is remarkably bipartisan.",
    copy: "Across political lines, voters prioritize the same fundamentals: accuracy, authenticity, and transparency.",
    insight: "One of the few trust expectations Americans do not polarize",
    mapLabel: "Midterm voting intention by congressional district",
    mapView: { variable: "pol_midterm_vote_choice", geoLevel: "cd", preferenceFilter: "top", viewMode: "top", topStrengthShading: false },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
  },
  {
    name: "close",
    headline: "Help shape what comes next.",
    copy: "",
    insight: "Tell us where to take the research next",
    mapLabel: "Authenticity messenger preference by CD",
    mapView: { variable: "nji_ad_authentic", geoLevel: "cd", preferenceFilter: "top", viewMode: "top", topStrengthShading: false },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1400 },
  },
];

const AD_VIEWS_OPTIONS = [
  {
    key: "ad_views.ad_accurate_pred_n",
    shortLabel: "Accuracy over AI origin",
    fullLabel: "What matters most is whether the message is accurate - not whether AI was used to create it.",
    color: "#4e79a7",
  },
  {
    key: "ad_views.ad_authentic_pred_n",
    shortLabel: "Authentic human stories",
    fullLabel: "Communications grounded in real people, authentic experiences, and true stories will become more valuable.",
    color: "#f28e2b",
  },
  {
    key: "ad_views.ad_quality_pred_n",
    shortLabel: "High-quality production",
    fullLabel: "What matters most is that the ad is high-quality and professionally made, however it's produced.",
    color: "#e15759",
  },
  {
    key: "ad_views.ad_transparent_pred_n",
    shortLabel: "Transparency about AI",
    fullLabel: "What matters most is that companies are transparent about when they use AI.",
    color: "#76b7b2",
  },
];

const AD_AUTHENTIC_OPTIONS = [
  {
    key: "ad_authentic.prof_actor_pred_n",
    shortLabel: "Professional actor",
    fullLabel: "It features a professional actor delivering a well-crafted message.",
    color: "#4e79a7",
  },
  {
    key: "ad_authentic.real_customer_pred_n",
    shortLabel: "Real customer or community member",
    fullLabel: "It features a real customer or community member sharing their own experience.",
    color: "#f28e2b",
  },
  {
    key: "ad_authentic.real_employee_pred_n",
    shortLabel: "Real employee, leader, or expert",
    fullLabel: "It features a real employee, leader, or expert with relevant knowledge.",
    color: "#e15759",
  },
  {
    key: "ad_authentic.realistic_ai_pred_n",
    shortLabel: "Realistic AI-generated person",
    fullLabel: "It features a realistic AI-generated person delivering the message.",
    color: "#76b7b2",
  },
];

const NEXT_RESEARCH_OPTIONS = [
  {
    key: "authenticity_threshold",
    label: "The authenticity threshold",
    detail: "Where do audiences draw the line between acceptable AI assistance and misleading use?",
  },
  {
    key: "creative_effectiveness",
    label: "Creative effectiveness",
    detail: "What makes creative feel authentic versus like “AI slop,” and what actually persuades?",
  },
  {
    key: "disclosure_perception",
    label: "Disclosure & perception",
    detail: "Does knowing, or simply believing, that AI was used change trust and effectiveness?",
  },
  {
    key: "policy_audiences",
    label: "Policy audiences",
    detail: "Do policymakers and opinion elites respond differently than voters?",
  },
  {
    key: "other",
    label: "Something else",
    detail: "Write in what you would want us to explore next.",
  },
];

const QUESTION_TEXT = {
  nji_ad_views: "Which statement comes closest to your view? As AI-generated ads and messages become more common:",
  nji_content_trust: "As AI-generated content becomes more common, which of the following do you think will matter most in determining whether you trust an organization's communications or advertising? Please rank all of the following in order of importance.",
  nji_ad_authentic: "Which of the following would most increase your trust that a company's advertisement or message is authentic? Please rank all of the following in order of importance.",
  nji_ad_reaction: "Suppose you saw an ad you liked, and later learned the person in it was AI-generated rather than a real person. Which best describes how you would react?",
};

const AppPanelWrapper = React.forwardRef(function AppPanelWrapper({ interactive = false }, ref) {
  const noopNavigate = useCallback(() => {}, []);
  return (
    <MainPage
      ref={ref}
      slug="nji"
      navigate={noopNavigate}
      hideChrome
      skipAuthRedirect
      disableMapInteraction={!interactive}
      geoPreloadOrder={["media_market"]}
    />
  );
});

let _cameraTimer = null;
let _flyTo2Timer = null;
let _lastFlyKey = "";
const RESET_HIGHLIGHTS_STATE = {
  gender: null,
  age: null,
  ethnicity: null,
  party: null,
  education: null,
  vote24: null,
  heatDomain: null,
  heatColor: null,
  topStrengthShading: false,
};

// MOBILE ONLY: fit camera moves with zero padding so the US fills the short
// strip (zooms in noticeably). sm+ passes through untouched (40px default).
function flyForViewport(fly) {
  return window.innerWidth < 600 ? { ...fly, padding: 0 } : fly;
}

function getGlMap(mainInst) {
  return mainInst?._mapPageInstance?.mapRef?.current?.getMap?.() || null;
}

function exitHighlights(navigate) {
  if (window.innerWidth < 600) {
    try { sessionStorage.setItem("m.activeScreen", "map"); } catch (e) { }
    navigate("/m");
  } else {
    navigate("/");
  }
}

function armCrossfade(glMap, duration = 550) {
  if (!glMap) return;
  let style;
  try { style = glMap.getStyle(); } catch { return; }
  if (!style?.layers) return;
  const t = { duration, delay: 0 };
  style.layers.forEach((layer) => {
    try {
      if (layer.type === "fill") {
        glMap.setPaintProperty(layer.id, "fill-color-transition", t);
        glMap.setPaintProperty(layer.id, "fill-opacity-transition", t);
        glMap.setPaintProperty(layer.id, "fill-outline-color-transition", t);
      } else if (layer.type === "line") {
        glMap.setPaintProperty(layer.id, "line-color-transition", t);
        glMap.setPaintProperty(layer.id, "line-opacity-transition", t);
      }
    } catch { }
  });
}

function navigateToStep(mainInst, stepName, extraMapState = {}) {
  if (!mainInst) return;
  const step = STEPS.find((s) => s.name === stepName);
  if (!step) return;

  if (_cameraTimer) { clearTimeout(_cameraTimer); _cameraTimer = null; }
  if (_flyTo2Timer) { clearTimeout(_flyTo2Timer); _flyTo2Timer = null; }

  mainInst.setState({ chatHighlightAreas: step.highlights || [] });

  const glMap = getGlMap(mainInst);
  armCrossfade(glMap);

  if (step.mapView) {
    mainInst.setHighlightsState({ ...RESET_HIGHLIGHTS_STATE, ...step.mapView, ...extraMapState });
  }

  if (step.flyTo) {
    const key = JSON.stringify(step.flyTo);
    if (key !== _lastFlyKey) {
      _lastFlyKey = key;
      _cameraTimer = setTimeout(() => {
        _cameraTimer = null;
        mainInst.flyTo(flyForViewport(step.flyTo));
      }, 80);
    }
  }

  if (step.flyTo2) {
    const delay = step.flyTo2Delay ?? 2600;
    _flyTo2Timer = setTimeout(() => {
      _flyTo2Timer = null;
      mainInst.flyTo(step.flyTo2);
      if (step.flyTo2GeoLevel || step.flyTo2MapView) {
        mainInst.setHighlightsState({
          geoLevel: step.flyTo2GeoLevel,
          ...(step.flyTo2MapView || {}),
        });
      }
    }, delay);
  }
}

// Height of the fixed mobile map strip. Shorter + full-width frames the US
// tighter (less Canada/Mexico from fitBounds padding on a tall aspect). The
// column pt, intro pull-up, and card scroll-margins below all derive from it.
const MOBILE_STRIP_H = "30dvh";

const F = "'Founders Grotesk', 'Work Sans', sans-serif";
const INTRO_SERIF = "'Editorial New', 'Playfair Display', Georgia, serif";
const NJI_BLUE = "#0F6EFC";
const NJI_BLACK = "#010203";
const TEAL      = "#0d9488";
const TEAL_DARK = "#0f766e";
const ACCENT    = "#06b6d4";
const NJI_BLUE_DARK = "#0B57CC";
const EMERALD   = "#059669";
const INK       = "#0f172a";
const MUTED     = "#475569";
const MUTED_LIGHT = "#94a3b8";
const PAPER     = "#ffffff";
const PAPER_SOFT = "linear-gradient(160deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.98) 100%)";
const BORDER    = "rgba(13,148,136,0.1)";
const BG        = "#ffffff";

const FADE_UP_KEYFRAMES = {
  "@keyframes fadeUpIn": {
    from: { opacity: 0, transform: "translateY(14px)" },
    to:   { opacity: 1, transform: "translateY(0)" },
  },
};
const fadeUp = (delay) => ({
  animation: `fadeUpIn 0.52s cubic-bezier(0.22,1,0.36,1) ${delay}s both`,
  ...FADE_UP_KEYFRAMES,
});

function NextStepPreview() { return null; }

function MapQuestionLegend({ config }) {
  if (!config) return null;
  const { question, legend } = config;
  return (
    <Box sx={{
      position: "absolute",
      bottom: 14,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 11,
      display: { xs: "none", sm: "flex" },
      flexDirection: "column",
      alignItems: "center",
      gap: 0.7,
      maxWidth: "min(600px, calc(100% - 170px))",
      px: 1.75,
      py: 1.1,
      borderRadius: "14px",
      bgcolor: "rgba(255,255,255,0.9)",
      backdropFilter: "blur(12px)",
      border: "1px solid rgba(15,23,42,0.08)",
      boxShadow: "0 10px 32px rgba(15,23,42,0.12)",
      textAlign: "center",
      animation: "fadeUpIn 0.4s ease both",
      ...FADE_UP_KEYFRAMES,
    }}>
      <Typography sx={{ fontSize: { xs: "13px", sm: "11px" }, fontWeight: 600, fontStyle: "italic", color: INK, fontFamily: F, lineHeight: 1.4 }}>
        {question}
      </Typography>
      {legend?.type === "categories" && (
        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 1.1, rowGap: 0.4 }}>
          {legend.items.map((item) => (
            <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: item.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: { xs: "11.5px", sm: "10px" }, fontWeight: 600, color: MUTED, fontFamily: F, lineHeight: 1.3 }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {legend?.type === "ramp" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", justifyContent: "center" }}>
          {legend.label && (
            <Typography sx={{ fontSize: { xs: "11.5px", sm: "10px" }, fontWeight: 600, color: MUTED, fontFamily: F }}>
              {legend.label}
            </Typography>
          )}
          <Typography sx={{ fontSize: { xs: "11.5px", sm: "9.5px" }, fontWeight: 700, color: MUTED_LIGHT, fontFamily: F }}>
            {legend.from || "Lower"}
          </Typography>
          <Box sx={{ width: 110, height: 8, borderRadius: "999px", background: `linear-gradient(90deg, ${legend.color}1c, ${legend.color})`, border: "1px solid rgba(15,23,42,0.06)" }} />
          <Typography sx={{ fontSize: { xs: "11.5px", sm: "9.5px" }, fontWeight: 700, color: MUTED_LIGHT, fontFamily: F }}>
            {legend.to || "Higher"}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function MapAgeBadge({ age }) {
  const option = TRUST_DRIVER_AGE_OPTIONS.find((o) => o.value === age);
  if (!option) return null;
  return (
    <Box sx={{
      position: "absolute",
      top: 14,
      right: 14,
      zIndex: 11,
      display: { xs: "none", sm: "flex" },
      alignItems: "center",
      gap: 0.85,
      px: 1.35,
      py: 0.9,
      borderRadius: "12px",
      bgcolor: "rgba(255,255,255,0.92)",
      backdropFilter: "blur(12px)",
      border: "1px solid rgba(15,23,42,0.08)",
      boxShadow: "0 10px 32px rgba(15,23,42,0.12)",
      animation: "fadeUpIn 0.4s ease both",
      ...FADE_UP_KEYFRAMES,
    }}>
      <Box>
        <Typography sx={{ fontSize: { xs: "10px", sm: "8.5px" }, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, lineHeight: 1.25 }}>
          Map showing
        </Typography>
        <Typography sx={{ fontSize: { xs: "13.5px", sm: "12px" }, fontWeight: 800, color: INK, fontFamily: F, lineHeight: 1.3 }}>
          {age == null ? "All ages" : `Ages ${option.label}`}
        </Typography>
      </Box>
    </Box>
  );
}

function DistrictPopover({ data }) {
  if (!data) return null;
  const { color, title, desc, districts, solid = false } = data;
  return (
    <Box sx={{
      display: { xs: "none", sm: "block" },
      position: "absolute", top: { xs: 12, sm: 18 }, left: { xs: 12, sm: 18 }, zIndex: 14,
      width: { xs: "calc(100% - 24px)", sm: 290 },
      maxHeight: "calc(100% - 90px)", overflowY: "auto",
      px: 1.5, py: 1.25, borderRadius: "12px",
      bgcolor: "rgba(255,255,255,0.95)",
      backdropFilter: "blur(12px)",
      border: solid ? `1px solid ${color}35` : `1px solid ${color}25`,
      boxShadow: "0 14px 36px rgba(15,23,42,0.14)",
      animation: "fadeUpIn 0.28s ease both", ...FADE_UP_KEYFRAMES,
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.5 }}>
        {/* <Box sx={{ width: 6, height: 6, borderRadius: "2px", bgcolor: color, flexShrink: 0 }} /> */}
        <Typography sx={{ fontSize: { xs: "13px", sm: "11px" }, fontWeight: 700, color, fontFamily: F, letterSpacing: "0.04em", textTransform: "uppercase" }}>
          {title}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: { xs: "13.5px", sm: "11.5px" }, color: solid ? color : MUTED, fontFamily: F, lineHeight: 1.4 }}>
        {desc}
      </Typography>
      {Array.isArray(districts) && districts.length > 0 && (
        <Box sx={{ display: { xs: "none", sm: "block" }, mt: 0.9, pt: 0.8, borderTop: solid ? `1px solid ${color}22` : `1px solid ${color}20` }}>
          <Typography sx={{ fontSize: { xs: "10.5px", sm: "9px" }, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: solid ? color : MUTED_LIGHT, fontFamily: F, mb: 0.5 }}>
            Top {districts.length} districts, highlighted on the map
          </Typography>
          <Box sx={{ display: "grid", gap: 0.3 }}>
            {districts.map((d, i) => (
              <Box key={d.id} sx={{ display: "flex", alignItems: "baseline", gap: 0.7 }}>
                <Typography sx={{ fontSize: { xs: "11.5px", sm: "10px" }, fontWeight: 700, color, fontFamily: F, minWidth: 16, textAlign: "right" }}>
                  {i + 1}.
                </Typography>
                <Typography sx={{ flex: 1, fontSize: { xs: "12px", sm: "10.5px" }, fontWeight: 600, color: solid ? color : INK, fontFamily: F, lineHeight: 1.4 }}>
                  {d.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

const INTRO_GEO_LEVELS = [
  { key: "states",       label: "State" },
  { key: "cd",           label: "CD" },
  { key: "media_market", label: "Media market" },
];

function IntroGeoToggle({ current, onChange, levels = INTRO_GEO_LEVELS }) {
  return (
    <Box sx={{
      position: "absolute", bottom: 16, left: 16, zIndex: 10,
      display: { xs: "none", sm: "flex" }, borderRadius: "10px", overflow: "hidden",
      border: `1px solid ${NJI_BLUE}28`,
      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    }}>
      {levels.map(({ key, label, xsLabel }) => {
        const active = current === key;
        return (
          <Box key={key} onClick={() => onChange(key)} sx={{
            px: 1.4, py: 0.75, cursor: "pointer",
            bgcolor: active ? NJI_BLUE : "rgba(255,255,255,0.94)",
            backdropFilter: "blur(10px)",
            color: active ? "#fff" : MUTED,
            fontSize: { xs: "13px", sm: "11px" }, fontWeight: 700, fontFamily: F, letterSpacing: "0.03em",
            transition: "all 0.18s ease", userSelect: "none",
            "&:hover": { bgcolor: active ? NJI_BLUE_DARK : `${NJI_BLUE}0e` },
          }}>
            {xsLabel ? (
              <>
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{label}</Box>
                <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>{xsLabel}</Box>
              </>
            ) : label}
          </Box>
        );
      })}
    </Box>
  );
}

function StackLogo() {
  return (
    <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
      <img src="/static/images/logo.png" alt="District Profiles" style={{ height: 22, width: "auto", display: "block" }} />
    </Box>
  );
}

function IntroBrandLockup() {
  return (
    <Box sx={{ mb: { xs: 2.1, sm: 2.6 }, display: "inline-flex", alignItems: "flex-end", gap: 1.35 }}>
      <img src="/static/images/stack_logo.png" alt="Stack Data Strategy" style={{ height: 24, width: "auto", display: "block" }} />
      <Box sx={{ width: "1px", height: 20, bgcolor: "rgba(1,2,3,0.18)" }} />
      <img src="/static/images/NJI-Black-RGB.png" alt="NJI" style={{ height: 20, width: "auto", display: "block" }} />
    </Box>
  );
}

function Eyebrow({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "11.5px", sm: "10px" }, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: NJI_BLUE, fontFamily: F, mb: 1.4 }}>
      {children}
    </Typography>
  );
}

function Headline({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "21px", sm: "26px", md: "30px" }, fontWeight: 600, lineHeight: { xs: 1.16, md: 1.1 }, letterSpacing: "-0.02em", color: INK, fontFamily: INTRO_SERIF, mb: 1.3 }}>
      {children}
    </Typography>
  );
}

function BodyCopy({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "14.5px", sm: "13.5px", md: "14px" }, color: MUTED, lineHeight: 1.4, fontFamily: F, whiteSpace: "pre-line", letterSpacing: "-0.01em" }}>
      {children}
    </Typography>
  );
}

function Em({ children }) {
  return <Box component="strong" sx={{ fontWeight: 700, color: INK }}>{children}</Box>;
}

// `mobileChildren` swaps in touch-appropriate wording on xs, where the inline
// map is non-interactive (hover only works via the fullscreen expand button).
function ClickHint({ children, mobileChildren }) {
  return (
    <Box sx={{ mt: 1.6, px: 1.5, py: 1.1, borderRadius: "10px", bgcolor: `${NJI_BLUE}08`, border: `1px solid ${NJI_BLUE}22`, display: "flex", alignItems: "flex-start", gap: 1.1 }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={NJI_BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
        <path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
      </svg>
      <Typography sx={{ fontSize: { xs: "13.5px", sm: "12px" }, color: INK, lineHeight: 1.4, fontFamily: F, display: mobileChildren ? { xs: "none", sm: "block" } : "block" }}>
        {children}
      </Typography>
      {mobileChildren && (
        <Typography sx={{ fontSize: { xs: "13.5px", sm: "12px" }, color: INK, lineHeight: 1.4, fontFamily: F, display: { xs: "block", sm: "none" } }}>
          {mobileChildren}
        </Typography>
      )}
    </Box>
  );
}

const HintStrong = ({ children }) => <strong style={{ color: NJI_BLUE }}>{children}</strong>;

function Panel({ children, sx = {}, ...props }) {
  return (
    <Box {...props} sx={{ width: "100%", maxWidth: 590, p: { xs: 2, md: 2.5 }, borderRadius: "22px", bgcolor: PAPER, backgroundImage: PAPER_SOFT, border: `1px solid ${BORDER}`, boxShadow: "0 8px 28px rgba(13,148,136,0.06)", ...sx }}>
      {children}
    </Box>
  );
}

function StepHeader({ index, total, chapter, isActive, onScrollNext, hasNext, onScrollPrev }) {
  const hasPrev = index > 0 && typeof onScrollPrev === "function";
  return (
    // Hidden on xs: the fixed mobile bottom bar already carries Prev / position /
    // Next, so the in-card footer would duplicate it.
    <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1, mt: "auto", pt: 1.5, opacity: isActive ? 1 : 0, transition: "opacity 0.35s ease", minHeight: 28 }}>
      <Typography sx={{ fontSize: { xs: "11.5px", sm: "10px" }, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: NJI_BLUE, fontFamily: F }}>
        {index + 1} of {total}
      </Typography>
      {chapter && (
        <Typography sx={{ fontSize: { xs: "11.5px", sm: "10px" }, color: MUTED_LIGHT, fontFamily: F, letterSpacing: "0.02em" }}>
          · {chapter}
        </Typography>
      )}
      {hasPrev && (
        <Box
          onClick={onScrollPrev}
          sx={{
            ml: "auto", display: "inline-flex", alignItems: "center", gap: 0.9,
            cursor: "pointer", px: 1.75, py: 0.8, borderRadius: "999px",
            border: "1.5px solid rgba(1,2,3,0.16)", bgcolor: "transparent",
            transition: "all 0.18s ease",
            "&:hover": { bgcolor: "rgba(1,2,3,0.04)", borderColor: "rgba(1,2,3,0.3)" },
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={NJI_BLACK} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 6 9 12 15 18" />
          </svg>
          <Typography sx={{ fontSize: { xs: "13px", sm: "11px" }, fontWeight: 700, color: NJI_BLACK, fontFamily: F, lineHeight: 1 }}>
            Back
          </Typography>
        </Box>
      )}
      {hasNext && (
        <Box
          onClick={onScrollNext}
          sx={{
            ml: hasPrev ? 0 : "auto", display: "inline-flex", alignItems: "center", gap: 0.9,
            cursor: "pointer", px: 1.75, py: 0.8, borderRadius: "999px",
            border: `1.5px solid ${NJI_BLUE}`, bgcolor: PAPER,
            transition: "all 0.18s ease",
            "&:hover": { bgcolor: `${NJI_BLUE}0d`, borderColor: NJI_BLUE_DARK },
          }}
        >
          <Typography sx={{ fontSize: { xs: "13px", sm: "11px" }, fontWeight: 700, color: NJI_BLACK, fontFamily: F, lineHeight: 1 }}>
            Next
          </Typography>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={NJI_BLACK} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 6 15 12 9 18" />
          </svg>
        </Box>
      )}
    </Box>
  );
}

const CHAPTER_NAMES = {
  "intro":         "The Research",
  "trust-drivers": "Trust Drivers",
  "messengers":    "The Messenger",
  "ai-reaction":   "The AI Penalty",
  "bipartisan":    "Bipartisan Alignment",
  "close":         "Survey",
};

function IntroCard({ step, nextStep, onScrollNext }) {
  const methodCards = [
    { title: "The sample", desc: "8,000 U.S. voters surveyed in July 2026." },
    { title: "The approach", desc: "Responses modeled to reflect communities nationwide." },
    { title: "The output", desc: "Trends and insights across every state, county, congressional district, and state legislative district." },
  ];

  return (
    <>
      <Box sx={fadeUp(0.05)}><IntroBrandLockup /></Box>
      <Box sx={{ ...fadeUp(0.1), maxWidth: 900, mx: "auto" }}>
        <Typography sx={{ fontSize: { xs: "11.5px", sm: "10px" }, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", color: NJI_BLUE, fontFamily: F, mb: 1.3 }}>
          The Insights
        </Typography>
        <Typography sx={{ fontSize: { xs: "38px", sm: "48px", md: "58px" }, fontWeight: 500, lineHeight: { xs: 1.03, md: 1.02 }, letterSpacing: "-0.04em", color: NJI_BLACK, fontFamily: INTRO_SERIF, mb: 1.8 }}>
          {step.headline}
        </Typography>
        <Typography sx={{ fontSize: { xs: "15px", md: "19px" }, fontWeight: 400, color: "rgba(1,2,3,0.66)", fontFamily: F, lineHeight: 1.4, maxWidth: 820, mx: "auto" }}>
          {step.copy}
        </Typography>
      </Box>

      <Box sx={{ ...fadeUp(0.2), mt: { xs: 3, md: 3.6 } }}>
        <Box sx={{
          position: "relative", overflow: "hidden",
          px: { xs: 2.1, sm: 2.5 }, py: { xs: 1.9, sm: 2.25 }, borderRadius: "20px",
          bgcolor: "rgba(255,255,255,0.78)",
          border: "1px solid rgba(1,2,3,0.08)",
          boxShadow: "0 14px 36px rgba(1,2,3,0.05)",
        }}>
          <Box sx={{ position: "relative" }}>
            <Typography sx={{ fontSize: { xs: "11.5px", sm: "10px" }, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: NJI_BLUE, fontFamily: F, mb: 0.9 }}>
              What we found
            </Typography>
            <Typography sx={{ fontSize: { xs: "26px", sm: "30px", md: "34px" }, fontWeight: 500, color: NJI_BLACK, fontFamily: INTRO_SERIF, lineHeight: 1.12, letterSpacing: "-0.03em", mb: 0.95 }}>
              The more AI creates, the more authenticity matters.
            </Typography>
            <Typography sx={{ fontSize: { xs: "13px", md: "15px" }, color: "rgba(1,2,3,0.66)", fontFamily: F, lineHeight: 1.4 }}>
              Real stories, credible messengers, and genuine human experiences become stronger differentiators.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ ...fadeUp(0.3), mt: 2.3 }}>
        <Typography sx={{ fontSize: { xs: "11.5px", sm: "10px" }, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(1,2,3,0.38)", fontFamily: F, mb: 1.15 }}>How We Did It</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: 1.1 }}>
          {methodCards.map((c, i) => (
            <Box
              key={c.title}
              sx={{
                position: "relative", overflow: "hidden", textAlign: "left",
                px: 1.6, pt: 1.5, pb: 1.4, borderRadius: "16px",
                border: `1px solid ${NJI_BLUE}`,
                bgcolor: "rgba(255,255,255,0.82)",
                boxShadow: "0 8px 24px rgba(1,2,3,0.04)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.7, mb: 0.55 }}>
                <Typography sx={{ fontSize: { xs: "11.5px", sm: "10px" }, fontWeight: 800, color: NJI_BLUE, fontFamily: F, letterSpacing: "0.08em" }}>{`0${i + 1}`}</Typography>
                <Typography sx={{ fontSize: { xs: "13.5px", sm: "11.5px" }, fontWeight: 700, color: NJI_BLACK, fontFamily: F, letterSpacing: "0.01em" }}>{c.title}</Typography>
              </Box>
              <Typography sx={{ fontSize: { xs: "13.5px", sm: "12px" }, color: "rgba(1,2,3,0.6)", fontFamily: F, lineHeight: 1.4 }}>{c.desc}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        onClick={onScrollNext}
        sx={{ ...fadeUp(0.38), mt: 2.8, display: { xs: "none", sm: "flex" }, alignItems: "center", justifyContent: "center", gap: 1, cursor: "pointer", userSelect: "none", "&:hover .scroll-label": { color: "rgba(1,2,3,0.68)" }, "&:hover .scroll-arrow": { borderColor: "rgba(15,110,252,0.48)", bgcolor: "rgba(255,255,255,0.9)" } }}
      >
        <Box className="scroll-arrow" sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: "50%", border: "1px solid rgba(15,110,252,0.28)", bgcolor: "rgba(255,255,255,0.7)", transition: "border-color 0.2s ease, background-color 0.2s ease", animation: "scrollBounce 1.6s ease-in-out 1.5s infinite", "@keyframes scrollBounce": { "0%,100%": { transform: "translateY(0)" }, "50%": { transform: "translateY(5px)" } } }}>
          <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M2 3.5L5 6.5L8 3.5" stroke={NJI_BLUE} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </Box>
        <Typography className="scroll-label" sx={{ fontSize: { xs: "13px", sm: "11px" }, fontWeight: 600, color: "rgba(1,2,3,0.38)", fontFamily: F, letterSpacing: "0.1em", textTransform: "uppercase", transition: "color 0.2s ease" }}>Scroll for the first finding</Typography>
      </Box>
      <NextStepPreview nextStep={nextStep} />
    </>
  );
}

function InsightBox({ color, title, stat, desc }) {
  return (
    <Box sx={{ height: "100%", px: 1.5, py: 1.25, borderRadius: "12px", border: `1px solid ${color}25`, bgcolor: PAPER, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.5 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: "2px", bgcolor: color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: { xs: "13px", sm: "11px" }, fontWeight: 700, color, fontFamily: F, letterSpacing: "0.04em", textTransform: "uppercase" }}>{title}</Typography>
      </Box>
      {stat && <Typography sx={{ fontSize: "22px", fontWeight: 800, color: INK, fontFamily: F, lineHeight: 1, letterSpacing: "-0.03em", mb: 0.5 }}>{stat}</Typography>}
      <Typography sx={{ fontSize: { xs: "13.5px", sm: "11.5px" }, color: MUTED, fontFamily: F, lineHeight: 1.4 }}>{desc}</Typography>
    </Box>
  );
}

function MethodNote() {
  return (
    <Typography sx={{ mt: 1.25, fontSize: { xs: "11.5px", sm: "9.5px" }, color: MUTED_LIGHT, fontFamily: F, lineHeight: 1.4 }}>
      MRP estimates, n=8,000, July 2026.
    </Typography>
  );
}

function StatRow({ label, value, color, sublabel }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ width: 7, height: 7, borderRadius: "2px", bgcolor: color, flexShrink: 0 }} />
      <Typography sx={{ flex: 1, fontSize: { xs: "13.5px", sm: "11.5px" }, fontWeight: 600, color: INK, fontFamily: F, lineHeight: 1.35 }}>
        {label}
      </Typography>
      <Box sx={{ textAlign: "right", minWidth: 52 }}>
        <Typography sx={{ fontSize: "16px", fontWeight: 800, color, fontFamily: F, lineHeight: 1 }}>
          {value}
        </Typography>
        {sublabel && (
          <Typography sx={{ fontSize: { xs: "10px", sm: "8.5px" }, color: MUTED_LIGHT, fontFamily: F, lineHeight: 1.2, mt: 0.15 }}>
            {sublabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function MirroredBarRow({
  label,
  detail,
  leftValue,
  rightValue,
  leftColor = "#2563eb",
  rightColor = "#dc2626",
}) {
  return (
    <Box>
      <Typography sx={{ fontSize: { xs: "13.5px", sm: "12px" }, fontWeight: 600, color: INK, fontFamily: F, lineHeight: 1.4 }}>
        {label}
      </Typography>
      {detail && (
        <Typography sx={{ fontSize: { xs: "11.5px", sm: "10px" }, fontWeight: 500, fontStyle: "italic", color: MUTED_LIGHT, fontFamily: F, lineHeight: 1.4, mt: 0.2 }}>
          {detail}
        </Typography>
      )}
      <Box sx={{ mt: 0.55, display: "grid", gridTemplateColumns: "34px 1fr 11px 1fr 34px", gap: 0.5, alignItems: "center" }}>
        <Typography sx={{ fontSize: { xs: "13px", sm: "11px" }, fontWeight: 700, color: leftColor, fontFamily: F, textAlign: "right" }}>
          {leftValue}%
        </Typography>
        <Box sx={{ height: 7, borderRadius: "999px", bgcolor: "rgba(37,99,235,0.12)", overflow: "hidden", transform: "scaleX(-1)" }}>
          <Box sx={{ width: `${leftValue}%`, height: "100%", bgcolor: leftColor, borderRadius: "999px" }} />
        </Box>
        <Box sx={{ justifySelf: "center", width: "1px", height: 13, bgcolor: BORDER }} />
        <Box sx={{ height: 7, borderRadius: "999px", bgcolor: "rgba(220,38,38,0.12)", overflow: "hidden" }}>
          <Box sx={{ width: `${rightValue}%`, height: "100%", bgcolor: rightColor, borderRadius: "999px", opacity: 0.78 }} />
        </Box>
        <Typography sx={{ fontSize: { xs: "13px", sm: "11px" }, fontWeight: 700, color: rightColor, fontFamily: F }}>
          {rightValue}%
        </Typography>
      </Box>
    </Box>
  );
}


const TRUST_DRIVER_AGE_OPTIONS = [
  { value: null, label: "Everyone" },
  { value: 1, label: "18–34" },
  { value: 2, label: "35–64" },
  { value: 3, label: "65+" },
];

const TRUST_DRIVER_BY_AGE = {
  null: {
    "ad_views.ad_accurate_pred_n": { pct: 34, count: 370 },
    "ad_views.ad_authentic_pred_n": { pct: 29, count: 29 },
    "ad_views.ad_transparent_pred_n": { pct: 24, count: 37 },
    "ad_views.ad_quality_pred_n": { pct: 14, count: 0 },
  },
  1: {
    "ad_views.ad_accurate_pred_n": { pct: 24, count: 134 },
    "ad_views.ad_authentic_pred_n": { pct: 30, count: 186 },
    "ad_views.ad_transparent_pred_n": { pct: 25, count: 116 },
    "ad_views.ad_quality_pred_n": { pct: 20, count: 0 },
  },
  2: {
    "ad_views.ad_accurate_pred_n": { pct: 32, count: 373 },
    "ad_views.ad_authentic_pred_n": { pct: 30, count: 35 },
    "ad_views.ad_transparent_pred_n": { pct: 23, count: 28 },
    "ad_views.ad_quality_pred_n": { pct: 13, count: 0 },
  },
  3: {
    "ad_views.ad_accurate_pred_n": { pct: 44, count: 418 },
    "ad_views.ad_authentic_pred_n": { pct: 26, count: 4 },
    "ad_views.ad_transparent_pred_n": { pct: 25, count: 14 },
    "ad_views.ad_quality_pred_n": { pct: 5, count: 0 },
  },
};

const TRUST_DRIVER_OVERLAY_TEXT = {
  "ad_views.ad_accurate_pred_n": {
    color: AD_VIEWS_OPTIONS[0].color,
    title: "Accuracy-led districts",
    desc: "Accuracy is the national default: it leads in 370 of 435 districts, and the map shades where its margin runs widest. It is also an age story: 44% of voters 65+ put accuracy first, versus 24% of 18–34s.",
  },
  "ad_views.ad_quality_pred_n": {
    color: AD_VIEWS_OPTIONS[2].color,
    title: "High-quality production",
    desc: "Production quality tops the list in zero of 435 districts. The map shades where its 13% national base runs strongest, tilting younger: 20% of 18–34s rank it first, versus 5% of voters 65+.",
  },
  "ad_views.ad_authentic_pred_n": {
    color: AD_VIEWS_OPTIONS[1].color,
    title: "Authenticity-led districts",
    desc: "The authenticity-led districts concentrate in majority-Hispanic communities: El Paso (TX-16), Miami (FL-26, FL-27, FL-28), San Antonio, and the Bronx. Demographics drive the pattern, and among 18–34s alone, authenticity leads in 186 districts, ahead of accuracy's 134.",
  },
  "ad_views.ad_transparent_pred_n": {
    color: AD_VIEWS_OPTIONS[3].color,
    title: "Transparency-led districts",
    desc: "Transparency-first wins the most-degreed metros: San Francisco, Manhattan, Cambridge, Seattle, and Austin. These are disclosure-sensitive audiences, but again the social composition comes first.",
  },
};

const DEFAULT_TRUST_DRIVERS_AGE = 1;

function TrustDriversCard({ step, isActive, nextStep, mainRef, onDistrictPopover, age, setAge }) {
  const [selected, setSelected] = useState(null);

  const pushMap = useCallback((option, ageVal, patch = {}) => {
    const inst = mainRef?.current;
    if (!inst) return;
    armCrossfade(getGlMap(inst));
    inst.setState({ chatHighlightAreas: [] });
    inst.setHighlightsState({
      variable: "nji_ad_views",
      geoLevel: "cd",
      preferenceFilter: option ? option.key : "top",
      viewMode: option ? "heat" : "top",
      topStrengthShading: false,
      gender: null, age: ageVal, ethnicity: null, party: null, education: null,
      ...patch,
    });
  }, [mainRef]);

  const handleSelect = (option) => {
    setSelected((prev) => {
      const next = prev?.key === option.key ? null : option;
      pushMap(next, age);
      onDistrictPopover?.(next ? TRUST_DRIVER_OVERLAY_TEXT[next.key] || null : null);
      return next;
    });
  };

  const handleAgeChange = (nextAge) => {
    setAge(nextAge);
    pushMap(selected, nextAge);
  };

  useEffect(() => {
    if (isActive) return;
    setSelected(null);
    setAge(DEFAULT_TRUST_DRIVERS_AGE);
    onDistrictPopover?.(null);
  }, [isActive, onDistrictPopover]);

  const rows = AD_VIEWS_OPTIONS
    .map((option) => ({ option, ...TRUST_DRIVER_BY_AGE[age][option.key] }))
    .sort((a, b) => b.pct - a.pct);

  return (
    <>
      <StackLogo />
      <Eyebrow>Trust Drivers</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      <Box sx={{ mt: 1.3, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, boxShadow: "0 8px 24px rgba(13,148,136,0.05)" }}>
        <Box sx={{ px: 1.15, py: 1.15, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: { xs: "11.5px", sm: "9.5px" }, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 0.55 }}>
            Survey question
          </Typography>
          <Typography sx={{ fontSize: { xs: "14px", sm: "12.5px" }, fontWeight: 600, color: INK, fontFamily: F, lineHeight: 1.4, fontStyle: "italic" }}>
            {QUESTION_TEXT.nji_ad_views}
          </Typography>
        </Box>

        <Box sx={{ px: 1.15, pt: 1.05, pb: 0.7, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 0.6 }}>
          <Typography sx={{ fontSize: { xs: "11.5px", sm: "9.5px" }, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: NJI_BLUE, fontFamily: F }}>
            Responses
          </Typography>
          <Box sx={{ display: "flex", gap: 0.4, flexWrap: "wrap" }}>
            {TRUST_DRIVER_AGE_OPTIONS.map((opt) => {
              const on = age === opt.value;
              return (
                <Box
                  key={String(opt.value)}
                  onClick={() => handleAgeChange(opt.value)}
                  sx={{
                    px: { xs: 1.4, sm: 1.15 }, py: { xs: 0.9, sm: 0.5 }, borderRadius: "999px", cursor: "pointer", userSelect: "none",
                    fontSize: { xs: "13px", sm: "11px" }, fontWeight: 700, fontFamily: F, lineHeight: 1,
                    bgcolor: on ? NJI_BLUE : "transparent",
                    color: on ? "#fff" : MUTED,
                    border: `1px solid ${on ? NJI_BLUE : "rgba(15,23,42,0.12)"}`,
                    transition: "all 0.18s ease",
                    "&:hover": { borderColor: NJI_BLUE, color: on ? "#fff" : NJI_BLUE },
                  }}
                >
                  {opt.label}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ px: 1.15, pb: 1.4, display: "grid", gap: 0.6 }}>
          {rows.map(({ option, pct }) => {
            const isSelected = selected?.key === option.key;
            return (
              <Box
                key={option.key}
                onClick={() => handleSelect(option)}
                sx={{
                  px: 0.85, py: 0.85, borderRadius: "12px", cursor: "pointer",
                  border: `1px solid ${isSelected ? option.color : "rgba(15,23,42,0.09)"}`,
                  bgcolor: isSelected ? `${option.color}12` : "transparent",
                  transition: "all 0.18s ease",
                  "&:hover": { borderColor: option.color, bgcolor: `${option.color}0a` },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.7 }}>
                  <Box sx={{ width: 8, height: 8, mt: 0.5, borderRadius: "2px", bgcolor: option.color, flexShrink: 0 }} />
                  <Typography sx={{ flex: 1, minWidth: 0, fontSize: { xs: "13.5px", sm: "12px" }, fontWeight: 600, color: INK, fontFamily: F, lineHeight: 1.4 }}>
                    {option.fullLabel}
                  </Typography>
                </Box>

                <Box sx={{ mt: 0.6, display: "flex", alignItems: "center", gap: 0.9 }}>
                  <Box sx={{ flex: 1, minWidth: 0, height: 5, borderRadius: "999px", bgcolor: `${option.color}14`, overflow: "hidden" }}>
                    <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: option.color, borderRadius: "999px", transition: "width 0.45s cubic-bezier(0.22,1,0.36,1)" }} />
                  </Box>
                  <Typography sx={{ fontSize: "17px", fontWeight: 800, color: option.color, fontFamily: F, lineHeight: 1, flexShrink: 0, minWidth: 40, textAlign: "right" }}>
                    {pct}%
                  </Typography>
                </Box>

                {isSelected && (
                  <Typography sx={{ display: { xs: "none", sm: "block" }, mt: 0.5, fontSize: { xs: "11.5px", sm: "9.5px" }, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: option.color, fontFamily: F }}>
                    Map shaded by strength of this view
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <ClickHint
        mobileChildren={
          <>
            <HintStrong>Tap a response</HintStrong> to map where it&rsquo;s strongest above, <HintStrong>switch the age filter</HintStrong> to see how rankings change, and <HintStrong>tap the expand button</HintStrong> to zoom the map.
          </>
        }
      >
        <HintStrong>Click a response</HintStrong> to map where it&rsquo;s strongest, <HintStrong>hover any district</HintStrong> for local results, and <HintStrong>switch the age filter</HintStrong> to see how rankings change.
      </ClickHint>

      <NextStepPreview nextStep={nextStep} />
    </>
  );
}

function AIReactionCard({ step, isActive, nextStep, mainRef }) {
  return (
    <>
      <StackLogo />
      <Eyebrow>The AI Penalty</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>
        A synthetic person is not inherently disqualifying. <Em>Trust breaks when audiences believe something is real and later learn it was AI-generated</Em>, a penalty that holds across nearly every district in America.
      </BodyCopy>

      <Box sx={{ mt: 2, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        <Box sx={{ px: 2, py: 1.4, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: { xs: "10.5px", sm: "9px" }, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 0.6 }}>
            Survey question
          </Typography>
          <Typography sx={{ fontSize: { xs: "14px", sm: "12.5px" }, fontWeight: 600, color: INK, fontFamily: F, lineHeight: 1.4, fontStyle: "italic" }}>
            {QUESTION_TEXT.nji_ad_reaction}
          </Typography>
        </Box>
        <Box sx={{ px: 2, pt: 1.25 }}>
          <Typography sx={{ fontSize: { xs: "10.5px", sm: "9px" }, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: NJI_BLUE, fontFamily: F }}>
            Responses
          </Typography>
        </Box>
        <Box sx={{ px: 2, pt: 1, pb: 1.6, display: "grid", gap: 0.75 }}>
          <StatRow label="Would trust the brand less" value="43%" color="#e11d48" />
          <StatRow label="Wouldn't be bothered" value="34%" color={EMERALD} />
          <StatRow label="Would stop buying" value="10%" color="#991b1b" />
        </Box>
      </Box>

      <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.1, alignItems: "stretch" }}>
        <InsightBox
          color="#e11d48"
          title="Even here"
          stat="CA-17 · 64%"
          desc="The strongest trust penalty comes from Silicon Valley itself. In CA-17, 64% would react negatively overall, trusting the brand less or stopping buying entirely, after learning the person was AI-generated. Those closest to AI aren't necessarily the most willing to be impersonated by it."
        />
        <InsightBox
          color="#e11d48"
          title="Inside the Beltway"
          stat="DC · 55%"
          desc="The capital is no exception. In Washington, DC, 55% would trust the brand less or stop buying, and 30% say a synthetic person would not bother them. The audience closest to regulation applies the same standard as everyone else."
        />
      </Box>

      <NextStepPreview nextStep={nextStep} />
    </>
  );
}

const MESSENGER_STAGE_OVERLAY = {
  winner: {
    color: NJI_BLUE,
    solid: true,
    title: "One human winner",
    desc: "The entire map resolves into a real human voice. There is no state or district in America where the AI-generated person wins.",
  },
  customer: {
    color: AD_AUTHENTIC_OPTIONS[1].color,
    title: "Customer-first regions",
    desc: 'Customers win across the Southeast and Mountain West, where peer voices carry more weight: “someone like me” is the trusted messenger.',
  },
  employee: {
    color: AD_AUTHENTIC_OPTIONS[2].color,
    title: "Employee and expert-first regions",
    desc: "Employees and experts win New York, the Great Lakes, and California, where authority and relevant knowledge matter more than peer identification.",
  },
};

function MessengerCard({ step, isActive, nextStep, mainRef, stage, setStage, geo, setGeo }) {
  useEffect(() => {
    if (!isActive) return;
    const inst = mainRef?.current;
    if (!inst) return;
    armCrossfade(getGlMap(inst), 900);
    inst.setState({ chatHighlightAreas: [] });
    // Full RESET on every push: this card's effect races navigateToStep on
    // slide entry, and without clearing the demographic slice it could carry
    // slide 2's age cut onto the messenger map (stuck "18-34" view).
    if (stage === "winner") {
      inst.setHighlightsState({ ...RESET_HIGHLIGHTS_STATE, variable: "nji_ad_authentic", geoLevel: geo, preferenceFilter: "top", viewMode: "top", topStrengthShading: false });
      return;
    }
    inst.setHighlightsState({
      ...RESET_HIGHLIGHTS_STATE,
      variable: "nji_ad_authentic",
      geoLevel: geo,
      preferenceFilter: stage === "customer" ? "ad_authentic.real_customer_pred_n" : "ad_authentic.real_employee_pred_n",
      viewMode: "heat",
    });
  }, [isActive, mainRef, stage, geo]);

  useEffect(() => {
    if (isActive) return;
    setStage("winner");
    setGeo("cd");
  }, [isActive, setStage, setGeo]);

  return (
    <>
      <StackLogo />
      <Eyebrow>The Messenger</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>
        Real people are the strongest signal of authenticity. Audiences prefer employees, experts, and customers with genuine experience over AI-generated people or professional actors, a preference that holds across every district in America.
      </BodyCopy>

      <Box sx={{ mt: 1.4, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        <Box sx={{ px: 2, py: 1.4, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: { xs: "10.5px", sm: "9px" }, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 0.6 }}>
            Survey question
          </Typography>
          <Typography sx={{ fontSize: { xs: "14px", sm: "12.5px" }, fontWeight: 600, color: INK, fontFamily: F, lineHeight: 1.4, fontStyle: "italic" }}>
            {QUESTION_TEXT.nji_ad_authentic}
          </Typography>
        </Box>

        <Box sx={{ px: 2, pt: 1.25 }}>
          <Typography sx={{ fontSize: { xs: "10.5px", sm: "9px" }, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: NJI_BLUE, fontFamily: F }}>
            Responses
          </Typography>
        </Box>

        <Box sx={{ px: 2, pt: 1, pb: 1.6, display: "grid", gap: 0.75, borderBottom: `1px solid ${BORDER}` }}>
          <StatRow label="Real employee, leader, or expert" value="38%" color={AD_AUTHENTIC_OPTIONS[2].color} />
          <StatRow label="Real customer or community member" value="36%" color={AD_AUTHENTIC_OPTIONS[1].color} />
          <StatRow label="Realistic AI-generated person" value="14%" color={AD_AUTHENTIC_OPTIONS[3].color} />
          <StatRow label="Professional actor" value="11%" color={AD_AUTHENTIC_OPTIONS[0].color} />
        </Box>
      </Box>
      <Box sx={{ pt: 1.25 }}>
        <ClickHint
          mobileChildren={
            <>
              <HintStrong>Tap the expand button on the map above</HintStrong> to zoom into the district-level view.
            </>
          }
        >
        <HintStrong>Hover over the map</HintStrong> to see views by district. Use the <HintStrong>State / Congressional district toggle in the bottom left of the map</HintStrong> to change geography.
      </ClickHint>
        </Box>
      <MethodNote />

      <NextStepPreview nextStep={nextStep} />
    </>
  );
}

function BipartisanCard({ step, nextStep }) {
  return (
    <>
      <StackLogo />
      <Eyebrow>Bipartisan Alignment</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      <Box sx={{ mt: 1, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: { xs: "12px", sm: "10.5px" }, fontWeight: 800, color: "#2563eb", fontFamily: F }}>2024 Harris voters</Typography>
          <Typography sx={{ fontSize: { xs: "10.5px", sm: "9px" }, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F }}>Share of each group</Typography>
          <Typography sx={{ fontSize: { xs: "12px", sm: "10.5px" }, fontWeight: 800, color: "#dc2626", fontFamily: F }}>2024 Trump voters</Typography>
        </Box>

        <Box sx={{ px: 2, py: 1.4, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: { xs: "10.5px", sm: "9px" }, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 0.6 }}>
            Survey question
          </Typography>
          <Typography sx={{ fontSize: { xs: "14px", sm: "12.5px" }, fontWeight: 600, color: INK, fontFamily: F, lineHeight: 1.4, fontStyle: "italic" }}>
            {QUESTION_TEXT.nji_ad_views}
          </Typography>
        </Box>
        <Box sx={{ px: 2, pt: 1.35, pb: 1.5, display: "grid", gap: 1.25 }}>
          <MirroredBarRow label={AD_VIEWS_OPTIONS[0].fullLabel} leftValue={33} rightValue={36} />
          <MirroredBarRow label={AD_VIEWS_OPTIONS[1].fullLabel} leftValue={30} rightValue={27} />
          <MirroredBarRow label={AD_VIEWS_OPTIONS[3].fullLabel} leftValue={25} rightValue={24} />
          <MirroredBarRow label={AD_VIEWS_OPTIONS[2].fullLabel} leftValue={12} rightValue={13} />
        </Box>

        <Box sx={{ px: 2, py: 1.4, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: { xs: "10.5px", sm: "9px" }, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 0.6 }}>
            Survey question
          </Typography>
          <Typography sx={{ fontSize: { xs: "14px", sm: "12.5px" }, fontWeight: 600, color: INK, fontFamily: F, lineHeight: 1.4, fontStyle: "italic" }}>
            {QUESTION_TEXT.nji_ad_reaction}
          </Typography>
        </Box>
        <Box sx={{ px: 2, pt: 1.35, pb: 1.6 }}>
          <MirroredBarRow
            label="Would trust the brand less or stop buying"
            detail="Combined negative reaction after learning the person in the ad was AI-generated."
            leftValue={55}
            rightValue={52}
          />
        </Box>
      </Box>

      <MethodNote />
      <NextStepPreview nextStep={nextStep} />
    </>
  );
}

// Form state is LIFTED to the page (like trustDriverAge/messengerStage): the
// close hero's render branch remounts when isActive flips, and local state
// would wipe a half-typed answer on any observer flicker or keyboard resize.
function PlaybookCard({ survey, setSurvey }) {
  const navigate = useNavigate();
  const { selectedOption, writeIn, email, submitState } = survey;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const setSelectedOption = (v) => setSurvey((s) => ({ ...s, selectedOption: v }));
  const setWriteIn = (v) => setSurvey((s) => ({ ...s, writeIn: v }));
  const setEmail = (v) => setSurvey((s) => ({ ...s, email: v }));
  const setSubmitState = (v) => setSurvey((s) => ({ ...s, submitState: v }));
  const selectedIsOther = selectedOption === "other";

  const handleSubmit = async () => {
    const trimmedWriteIn = writeIn.trim();
    if (selectedIsOther && !trimmedWriteIn) {
      setSubmitState({ status: "error", message: "Add a short write-in so we know what you want explored next." });
      return;
    }
    const trimmedEmail = email.trim();
    if (trimmedEmail && !/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
      setSubmitState({ status: "error", message: "That email address doesn't look right." });
      return;
    }

    setIsSubmitting(true);
    setSubmitState({ status: "idle", message: "" });
    try {
      const selected = NEXT_RESEARCH_OPTIONS.find((option) => option.key === selectedOption);
      const res = await fetch("/api/research-feedback/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          prompt: "If we took this research further, what should we explore next?",
          choice_key: selectedOption,
          choice_label: selected?.label || selectedOption,
          write_in: trimmedWriteIn,
          email: trimmedEmail,
          source: "nji_creative_close",
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || "Something went wrong saving your response.");
      }
      setSubmitState({
        status: "success",
        message: trimmedEmail
          ? "Thanks, your response has been saved. We'll keep you posted on the research."
          : "Thanks, your response has been saved.",
      });
      if (!selectedIsOther) setWriteIn("");
    } catch (err) {
      setSubmitState({ status: "error", message: err?.message || "Something went wrong saving your response." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", width: "100%" }}>
        <IntroBrandLockup />
      </Box>

      <Box sx={{ mt: 1, width: "100%", borderRadius: "20px", border: "1px solid rgba(1,2,3,0.08)", bgcolor: "rgba(255,255,255,0.82)", boxShadow: "0 12px 32px rgba(1,2,3,0.05)", px: { xs: 1.45, sm: 2.2 }, py: { xs: 1.5, sm: 2 }, textAlign: "left" }}>
        <Typography sx={{ fontSize: { xs: "11.5px", sm: "10px" }, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: NJI_BLUE, fontFamily: F, mb: 0.75 }}>
          Survey
        </Typography>
        <Typography sx={{ fontSize: { xs: "23px", sm: "26px" }, fontWeight: 500, color: NJI_BLACK, fontFamily: INTRO_SERIF, lineHeight: 1.08, letterSpacing: "-0.03em", mb: 0.5 }}>
          Help shape what comes next.
        </Typography>
        <Typography sx={{ fontSize: { xs: "13.5px", sm: "11.5px" }, color: "rgba(1,2,3,0.6)", fontFamily: F, lineHeight: 1.4, mb: 1.2 }}>
          If we took this research further, where would you want it to go?
        </Typography>

        <Box sx={{ display: "grid", gap: 0.75 }}>
          {NEXT_RESEARCH_OPTIONS.map((option) => {
            const selected = selectedOption === option.key;
            return (
              <Box
                key={option.key}
                onClick={() => {
                  setSelectedOption(option.key);
                  if (submitState.status !== "idle") setSubmitState({ status: "idle", message: "" });
                }}
                sx={{
                  px: 1.15,
                  py: 0.95,
                  borderRadius: "14px",
                  border: `1px solid ${selected ? "rgba(15,110,252,0.26)" : "rgba(1,2,3,0.08)"}`,
                  bgcolor: selected ? "rgba(15,110,252,0.05)" : "rgba(255,255,255,0.72)",
                  cursor: "pointer",
                  transition: "all 0.18s ease",
                  "&:hover": { borderColor: "rgba(15,110,252,0.18)", bgcolor: "rgba(255,255,255,0.92)" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.8 }}>
                  <Box sx={{ width: 15, height: 15, mt: 0.15, borderRadius: "50%", border: `1.5px solid ${selected ? NJI_BLUE : "rgba(1,2,3,0.22)"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {selected && <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: NJI_BLUE }} />}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: { xs: "14px", sm: "12px" }, fontWeight: 700, color: NJI_BLACK, fontFamily: F, lineHeight: 1.4 }}>
                      {option.label}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "13px", sm: "12px" }, color: "rgba(1,2,3,0.58)", fontFamily: F, lineHeight: 1.4, mt: 0.2 }}>
                      {option.detail}
                    </Typography>
                    {option.key === "other" && selected && (
                      <TextField
                        autoFocus
                        multiline
                        minRows={2}
                        value={writeIn}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => {
                          setWriteIn(event.target.value);
                          if (submitState.status !== "idle") setSubmitState({ status: "idle", message: "" });
                        }}
                        placeholder="Tell us what you would want to explore next…"
                        sx={{
                          mt: 0.7,
                          width: "100%",
                          "& .MuiOutlinedInput-root": {
                            alignItems: "flex-start",
                            borderRadius: "10px",
                            bgcolor: "rgba(255,255,255,0.9)",
                            fontFamily: F,
                            fontSize: { xs: "16px", sm: "12.5px" },
                          },
                          "& .MuiOutlinedInput-input": { py: 0.2 },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(1,2,3,0.1)",
                          },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ mt: 1.2 }}>
          <Typography sx={{ fontSize: { xs: "13px", sm: "11px" }, fontWeight: 700, color: NJI_BLACK, fontFamily: F, mb: 0.35 }}>
            Want to stay in the loop?
          </Typography>
          <TextField
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (submitState.status !== "idle") setSubmitState({ status: "idle", message: "" });
            }}
            placeholder="Email address (optional)"
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                bgcolor: "rgba(255,255,255,0.84)",
                fontFamily: F,
              },
              // 16px on xs: anything smaller triggers iOS Safari's focus auto-zoom.
              "& .MuiOutlinedInput-input": { py: 1.1, fontSize: { xs: "16px", sm: "13px" } },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(1,2,3,0.1)",
              },
            }}
          />
          <Typography sx={{ mt: 0.4, fontSize: { xs: "11.5px", sm: "9.5px" }, color: "rgba(1,2,3,0.45)", fontFamily: F, lineHeight: 1.4 }}>
            We'll only use this to share future research from this series.
          </Typography>
        </Box>

        {submitState.message && (
          <Typography sx={{ mt: 0.8, fontSize: { xs: "12px", sm: "10.5px" }, color: submitState.status === "success" ? "#15803d" : "#b91c1c", fontFamily: F, lineHeight: 1.4 }}>
            {submitState.message}
          </Typography>
        )}

        <Box sx={{ mt: 1.1, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{ bgcolor: NJI_BLUE, color: "#fff", fontFamily: F, fontWeight: 700, fontSize: { xs: "15px", sm: "13px" }, textTransform: "none", borderRadius: "999px", px: { xs: 3, sm: 2.4 }, py: { xs: 1.1, sm: 0.9 }, boxShadow: "0 10px 22px rgba(15,110,252,0.18)", "&:hover": { bgcolor: "#0b5ad0" } }}
          >
            {isSubmitting ? "Saving…" : "Submit"}
          </Button>
        </Box>

        {submitState.status === "success" && (
          <Box sx={{ mt: 1.5, pt: 1.4, borderTop: "1px solid rgba(1,2,3,0.08)", display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, gap: 1.2, animation: "fadeUpIn 0.4s ease both", ...FADE_UP_KEYFRAMES }}>
            <Typography sx={{ flex: 1, fontSize: { xs: "13px", sm: "12px" }, color: "rgba(1,2,3,0.66)", fontFamily: F, lineHeight: 1.4 }}>
              Want to dig into the data behind this story? Create a free District Profiles demo account and explore every district yourself.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate("/?signup=1")}
              sx={{ borderColor: NJI_BLUE, color: NJI_BLUE, fontFamily: F, fontWeight: 700, fontSize: { xs: "14px", sm: "12.5px" }, textTransform: "none", borderRadius: "999px", px: 2, py: 0.7, whiteSpace: "nowrap", flexShrink: 0, "&:hover": { borderColor: "#0b5ad0", bgcolor: "rgba(15,110,252,0.05)" } }}
            >
              Create a demo account
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
}

function NarrativeCard({ step, index, total, isActive, cardRef, mainRef, onScrollNext, onScrollPrev, onPanelWheel, onDistrictPopover, trustDriverAge, setTrustDriverAge, messengerStage, setMessengerStage, messengerGeo, setMessengerGeo, survey, setSurvey }) {
  const nextStep = STEPS[index + 1];
  const isIntroHero = step.name === "intro";
  const isCloseHero = step.name === "close";
  const isBipartisanHero = step.name === "bipartisan";
  const isOverlayHero = isIntroHero || isCloseHero || isBipartisanHero;
  const heroMaxWidth = isIntroHero ? "min(920px, 94vw)" : isCloseHero ? 760 : "min(880px, 92vw)";

  // On xs the bipartisan AND close slides render as plain in-flow cards (the
  // hero panel clips, and the inactive-preview → active-panel swap flashed),
  // so only the intro keeps hero metrics there.
  const heroOnXs = isIntroHero;
  const cardSx = {
    // xs: every card grows with its content (heroes keep a full-screen minimum
    // but must never clip; the window scrolls). sm+: fixed-viewport snap panels.
    height: {
      xs: "auto",
      sm: "100vh",
    },
    minHeight: {
      xs: heroOnXs ? "100dvh" : "80vh",
      sm: isOverlayHero ? "100vh" : "auto",
    },
    flexShrink: 0,
    scrollSnapAlign: "start",
    scrollSnapStop: "always",
    // xs: the fixed MOBILE_STRIP_H map strip covers the top of the viewport, so scrolls
    // that anchor a card to the top would bury its headline behind the map.
    // scroll-margin keeps the card's start just below the strip; steps that
    // hide the strip on mobile (intro/bipartisan/close) anchor to the true top.
    scrollMarginTop: {
      xs: ["intro", "bipartisan", "close"].includes(step.name) ? 0 : MOBILE_STRIP_H,
      sm: 0,
    },
    // xs: cancels the column's constant strip-height pt so the landing card starts at
    // the true top (its map strip is hidden); minHeight 100dvh makes this safe.
    mt: { xs: isIntroHero ? `-${MOBILE_STRIP_H}` : 0, sm: 0 },
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    pt: { xs: heroOnXs ? 0 : 3.5, sm: isOverlayHero ? 0 : 6, md: isOverlayHero ? 0 : 7 },
    pb: { xs: 5, sm: 5, md: 7 },
    // xs: the landing hero runs edge-to-edge, the closing survey keeps a sliver
    // of side gutter, and other cards keep the full one.
    px: { xs: isIntroHero ? 0 : isCloseHero ? 1.25 : 2.5, sm: 3, md: 4.5 },
    position: "relative",
    overflowY: { xs: "visible", sm: isOverlayHero ? "hidden" : "auto" },
    borderLeft: "2px solid",
    borderColor: isOverlayHero ? "transparent" : (isActive ? `${NJI_BLUE}28` : "transparent"),
    opacity: isOverlayHero ? 1 : (isActive ? 1 : 0.38),
    transition: "opacity 0.4s ease, border-color 0.4s ease",
    background: isOverlayHero ? "transparent" : (isActive ? `linear-gradient(90deg, ${NJI_BLUE}05 0%, transparent 20%)` : "transparent"),
  };

  const inner = (() => {
    switch (step.name) {
      case "intro":              return <IntroCard step={step} nextStep={nextStep} onScrollNext={onScrollNext} />;
      case "trust-drivers":      return <TrustDriversCard step={step} isActive={isActive} nextStep={nextStep} mainRef={mainRef} onDistrictPopover={onDistrictPopover} age={trustDriverAge} setAge={setTrustDriverAge} />;
      case "ai-reaction":        return <AIReactionCard step={step} isActive={isActive} nextStep={nextStep} mainRef={mainRef} />;
      case "messengers":         return <MessengerCard step={step} isActive={isActive} nextStep={nextStep} mainRef={mainRef} stage={messengerStage} setStage={setMessengerStage} geo={messengerGeo} setGeo={setMessengerGeo} />;
      case "bipartisan":         return <BipartisanCard step={step} nextStep={nextStep} />;
      case "close":              return <PlaybookCard step={step} nextStep={nextStep} survey={survey} setSurvey={setSurvey} />;
      default: return <><Headline>{step.headline}</Headline>{step.copy && <BodyCopy>{step.copy}</BodyCopy>}</>;
    }
  })();

  return (
    <Box ref={cardRef} data-index={index} sx={cardSx}>
      {isOverlayHero && isActive ? (
        <>
        {(isBipartisanHero || isCloseHero) && (
          // Mobile fallback: the bipartisan and close heroes render as plain
          // in-flow cards on xs — same subtree as their scroll-by preview, so
          // activation never re-mounts them into a Panel (which flashed).
          <Box sx={{ display: { xs: "flex", sm: "none" }, flexDirection: "column", flex: 1, width: "100%" }}>
            {inner}
            {!isCloseHero && <StepHeader index={index} total={total} chapter={CHAPTER_NAMES[step.name]} isActive={isActive} hasNext={!!nextStep} onScrollNext={onScrollNext} onScrollPrev={onScrollPrev} />}
          </Box>
        )}
        <Box sx={{ display: { xs: (isBipartisanHero || isCloseHero) ? "none" : "flex", sm: "flex" }, position: { xs: "relative", sm: "fixed" }, inset: { xs: "auto", sm: "0 0 0 0" }, zIndex: 20, height: { xs: "auto", sm: "100vh" }, alignItems: "center", justifyContent: "center", px: { xs: 0, sm: 3, md: 5 }, py: { xs: 0, sm: 3 }, pointerEvents: "none", bgcolor: isCloseHero ? { xs: "transparent", sm: NJI_BLACK } : "transparent", animation: isCloseHero ? "heroBgIn 0.6s ease both" : "none", "@keyframes heroBgIn": { from: { opacity: 0 }, to: { opacity: 1 } } }}>
          <Panel
            data-hero-scroll="true"
            sx={{ maxWidth: { xs: "100%", sm: heroMaxWidth }, mx: "auto", maxHeight: { xs: "none", sm: "calc(100vh - 40px)" }, overflowY: { xs: "visible", sm: "auto" }, borderRadius: { xs: isIntroHero || isCloseHero ? 0 : "22px", sm: "22px" }, p: { xs: 2.5, sm: 3.5, md: isIntroHero ? 4 : 3.5 }, textAlign: isIntroHero ? "center" : "left", pointerEvents: "auto", bgcolor: isCloseHero ? "rgba(255,255,255,0.96)" : "rgba(255,255,255,0.78)", backgroundImage: "none", backdropFilter: "blur(20px)", border: { xs: isIntroHero || isCloseHero ? "none" : "1px solid rgba(1,2,3,0.06)", sm: "1px solid rgba(1,2,3,0.06)" }, boxShadow: "0 26px 64px rgba(1,2,3,0.08)", transition: "transform 0.7s cubic-bezier(0.22,1,0.36,1), opacity 0.7s cubic-bezier(0.22,1,0.36,1), box-shadow 0.7s ease" }}
          >
            <Box key="intro-active" sx={{ display: "flex", flexDirection: "column", width: "100%", animation: "cardEnter 0.6s cubic-bezier(0.22,1,0.36,1) both", "@keyframes cardEnter": { from: { opacity: 0.3, transform: "translateY(10px)" }, to: { opacity: 1, transform: "translateY(0)" } } }}>
              {inner}
              {!isCloseHero && <StepHeader index={index} total={total} chapter={CHAPTER_NAMES[step.name]} isActive={isActive} hasNext={!!nextStep} onScrollNext={onScrollNext} onScrollPrev={onScrollPrev} />}
            </Box>
          </Panel>
        </Box>
        </>
      ) : (
        <Box key={isActive ? "active" : "idle"} sx={{ display: "flex", flexDirection: "column", flex: 1, width: "100%", animation: isActive ? "cardEnter 0.45s cubic-bezier(0.22,1,0.36,1) both" : "none", "@keyframes cardEnter": { from: { opacity: 0.3, transform: "translateY(10px)" }, to: { opacity: 1, transform: "translateY(0)" } } }}>
          <Box sx={{ width: "100%", flex: { xs: "0 0 auto", sm: 1 }, display: "flex", flexDirection: "column", justifyContent: { xs: "flex-start", sm: "center" } }}>
            {inner}
          </Box>
          <StepHeader index={index} total={total} chapter={CHAPTER_NAMES[step.name]} isActive={isActive} hasNext={!!nextStep} onScrollNext={onScrollNext} onScrollPrev={onScrollPrev} />
        </Box>
      )}
    </Box>
  );
}

export default function NJICreativePage() {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [geoLevel, setGeoLevel] = useState("cd");
  const [mapReady, setMapReady] = useState(false);
  const [districtPopover, setDistrictPopover] = useState(null);
  const [trustDriverAge, setTrustDriverAge] = useState(DEFAULT_TRUST_DRIVERS_AGE);
  const [messengerStage, setMessengerStage] = useState("winner");
  const [messengerGeo, setMessengerGeo] = useState("cd");
  // Close-slide survey form state lives here so the hero branch remount
  // (isActive flips) can never wipe a half-typed response.
  const [survey, setSurvey] = useState({ selectedOption: "authenticity_threshold", writeIn: "", email: "", submitState: { status: "idle", message: "" } });
  const cardRefs = useRef([]);
  const mainRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const navTimerRef = useRef(null);
  const targetIndexRef = useRef(null);
  const unlockTimerRef = useRef(null);
  useEffect(() => () => { if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current); }, []);
  const visibleSet = useRef(new Set());
  const activeIndexRef = useRef(0);
  const mapPanelRef = useRef(null);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  // Tracks the xs/sm boundary so the scroll observer re-roots when the viewport
  // crosses it (devtools responsive mode; real devices never cross it).
  const [mobileMode, setMobileMode] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobileMode(window.innerWidth < 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isIntroStep = activeIndex === 0;
  const activeStep = STEPS[activeIndex];

  const isMapBackdropStep = isIntroStep || activeStep?.name === "bipartisan";

  // Mobile shows the map strip only on the map-driven middle slides;
  // the landing card and the final two (bipartisan, close) are cards-only on xs.
  const hideMapOnXs = isIntroStep || activeStep?.name === "bipartisan" || activeStep?.name === "close";

  // If the user expanded the map fullscreen and then reaches a strip-hidden
  // step, clear the latch: otherwise fullscreen stays true with no visible
  // toggle to exit it.
  useEffect(() => {
    if (hideMapOnXs) setMapFullscreen(false);
  }, [hideMapOnXs]);

  // Closing the fullscreen map must also dismiss any open tooltip: the tap
  // that opened it happened while interactive, and nothing else clears it
  // once interaction is disabled again.
  useEffect(() => {
    if (mapFullscreen) return;
    const mapPage = mainRef.current?._mapPageInstance;
    if (mapPage?.state?.hoverInfo) mapPage.setState({ hoverInfo: null });
  }, [mapFullscreen]);

  const mapFooterConfig = (() => {
    switch (activeStep?.name) {
    
      // case "messengers":
      //   return {
      //     question: QUESTION_TEXT.nji_ad_authentic,
      //     legend: messengerStage === "winner"
      //       ? { type: "categories", items: AD_AUTHENTIC_OPTIONS.map((o) => ({ color: o.color, label: o.shortLabel })) }
      //       : {
      //           type: "ramp",
      //           color: AD_AUTHENTIC_OPTIONS[messengerStage === "customer" ? 1 : 2].color,
      //           label: messengerStage === "customer" ? "Customer-first share" : "Employee-first share",
      //         },
      //   };
      case "ai-reaction":
        return {
          question: QUESTION_TEXT.nji_ad_reaction,
          legend: { type: "ramp", color: "#991b1b", from: "48%", to: "64%", label: "Would trust less or stop buying" },
        };
      default:
        return null;
    }
  })();

  const mapWidth = { xs: "100%", sm: isMapBackdropStep ? "100%" : "47%", md: isMapBackdropStep ? "100%" : "50%" };
  const cardMr   = { xs: 0, sm: isMapBackdropStep ? 0 : "47%", md: isMapBackdropStep ? 0 : "50%" };

  useEffect(() => {
    const t1 = setTimeout(() => { getGlMap(mainRef.current)?.resize(); }, 100);
    const t2 = setTimeout(() => { getGlMap(mainRef.current)?.resize(); }, 300);
    const t3 = setTimeout(() => {
      const glMap = getGlMap(mainRef.current);
      if (glMap) {
        glMap.resize();
        const step = STEPS[activeIndexRef.current];
        if (step?.flyTo) mainRef.current?.flyTo?.(flyForViewport(step.flyTo));
      }
    }, 500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [isMapBackdropStep]);

  const scrollToIndex = useCallback((i) => {
    const card = cardRefs.current[i];
    if (!card) return;
    const container = scrollContainerRef.current;
    if (container) container._scrollLocked = true;
    targetIndexRef.current = i;
    activeIndexRef.current = i;
    // Seed the mobile visibility set with the target so no stale index can win
    // Math.max after unlock (exit entries during the lock keep membership
    // current, but a re-tap mid-scroll could otherwise race).
    visibleSet.current = new Set([i]);
    setActiveIndex(i);
    card.scrollIntoView({ behavior: "smooth", block: "start" });
    // A re-tap within 1.2s must not let the OLD timer unlock mid-scroll.
    if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
    unlockTimerRef.current = window.setTimeout(() => {
      unlockTimerRef.current = null;
      if (container) container._scrollLocked = false;
      targetIndexRef.current = null;
      const el = cardRefs.current[i];
      if (!el) return;
      if (window.innerWidth >= 600 && container) {
        const offset = el.getBoundingClientRect().top - container.getBoundingClientRect().top;
        if (Math.abs(offset) > 4) el.scrollIntoView({ behavior: "auto", block: "start" });
      } else if (window.innerWidth < 600) {
        // xs re-anchor: the window is the scroller and the card should sit at
        // its scroll-margin (below the map strip on map steps, top otherwise).
        const expectedTop = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
        if (Math.abs(el.getBoundingClientRect().top - expectedTop) > 4) {
          el.scrollIntoView({ behavior: "auto", block: "start" });
        }
      }
    }, 1200);
  }, []);

  const getNearestCardIndex = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !cardRefs.current.length) return null;

    const containerTop = container.getBoundingClientRect().top;
    let nearestIdx = null;
    let nearestDistance = Infinity;

    cardRefs.current.forEach((card, idx) => {
      if (!card) return;
      const distance = Math.abs(card.getBoundingClientRect().top - containerTop);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIdx = idx;
      }
    });

    return nearestIdx;
  }, []);

  // Timestamp-based SLIDING cooldown: trackpad inertia can outlast any fixed
  // timer, and the leftover momentum events would re-accumulate and advance a
  // second slide from one physical flick. While events keep arriving during
  // cooldown, the re-arm keeps sliding back until the gesture truly ends.
  const wheelCooldownRef = useRef(false);
  const wheelDeltaRef = useRef(0);
  const lastWheelTsRef = useRef(0);
  const wheelNavAtRef = useRef(0);
  const handleRightPanelWheel = useCallback((e) => {
    if (window.innerWidth < 600) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const heroEl = e.target instanceof Element ? e.target.closest("[data-hero-scroll]") : null;
    if (heroEl instanceof HTMLElement) {
      const canScrollDown = heroEl.scrollTop + heroEl.clientHeight < heroEl.scrollHeight - 2;
      const canScrollUp = heroEl.scrollTop > 2;
      if ((e.deltaY > 0 && canScrollDown) || (e.deltaY < 0 && canScrollUp)) {
        wheelDeltaRef.current = 0;
        return;
      }
    }

    const cardEl = e.target instanceof Element ? e.target.closest("[data-index]") : null;
    if (cardEl instanceof HTMLElement) {
      const canScrollDown = cardEl.scrollTop + cardEl.clientHeight < cardEl.scrollHeight - 2;
      const canScrollUp = cardEl.scrollTop > 2;
      if ((e.deltaY > 0 && canScrollDown) || (e.deltaY < 0 && canScrollUp)) {
        wheelDeltaRef.current = 0;
        return;
      }
    }

    e.preventDefault();
    const now = Date.now();
    const gap = now - lastWheelTsRef.current;
    lastWheelTsRef.current = now;
    if (container._scrollLocked) return;
    if (wheelCooldownRef.current) {
      // Release on a 250ms pause in the wheel stream OR a 1.6s hard cap,
      // whichever comes first: the pause catches deliberate second flicks
      // instantly, the cap stops long free-spin inertia from locking the
      // reel up (native scroll is genuinely prevented now, so a rare
      // late re-nav is at worst intended input).
      const sinceNav = now - wheelNavAtRef.current;
      if (gap < 250 && sinceNav < 1600) return;
      wheelCooldownRef.current = false;
    }

    wheelDeltaRef.current += e.deltaY;
    if (Math.abs(wheelDeltaRef.current) < 70) return;

    const currentIdx = targetIndexRef.current ?? getNearestCardIndex();
    if (currentIdx == null) return;

    const dir = wheelDeltaRef.current > 0 ? 1 : -1;
    if (dir === 0) return;

    const nextIdx = Math.max(0, Math.min(STEPS.length - 1, currentIdx + dir));
    if (nextIdx === currentIdx) {
      wheelDeltaRef.current = 0;
      return;
    }

    wheelDeltaRef.current = 0;
    wheelCooldownRef.current = true;
    wheelNavAtRef.current = Date.now();
    scrollToIndex(nextIdx);
  }, [getNearestCardIndex, scrollToIndex]);

  // React's onWheel is attached PASSIVELY at the root, so preventDefault was
  // silently ignored: native inertia scroll ran alongside the programmatic
  // snap (double-advance on large screens, left side only — the map panel has
  // nothing to scroll natively). Attach non-passive listeners ourselves so
  // preventDefault actually blocks the native scroll. Column listener also
  // covers the hero panels via bubbling.
  useEffect(() => {
    const els = [scrollContainerRef.current, mapPanelRef.current].filter(Boolean);
    els.forEach((el) => el.addEventListener("wheel", handleRightPanelWheel, { passive: false }));
    return () => els.forEach((el) => el.removeEventListener("wheel", handleRightPanelWheel));
  }, [handleRightPanelWheel]);


  useEffect(() => {
    const step = STEPS[activeIndex];
    if (step?.mapView?.geoLevel) setGeoLevel(step.mapView.geoLevel);
  }, [activeIndex]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
    if (navTimerRef.current) clearTimeout(navTimerRef.current);
    navTimerRef.current = setTimeout(() => {
      navTimerRef.current = null;
      const stepName = STEPS[activeIndex]?.name;
      if (stepName) {
        navigateToStep(mainRef.current, stepName);
      }
    }, 160);
  }, [activeIndex]);

  useEffect(() => {
    let fired = false;
    const poll = setInterval(() => {
      const inst = mainRef.current;
      if (!inst) return;
      const loading = inst.state?.mapLoading;
      const mapPage = inst._mapPageInstance;
      const cdLoaded = mapPage?.state?.geoPreloadStatus?.cd === "loaded";
      if (!fired && loading === false && cdLoaded) {
        // If the visible geometry isn't the CD layer (e.g. the mount-time
        // states load resolved after the cd load and overwrote it), reload cd.
        // loadGeo is cache-first, so this is cheap when already cached. NOTE:
        // cd cache keys carry the boundary vintage ("cd::CD120"), so match by
        // prefix; a literal "cd:" lookup never matches and disabled this
        // recovery entirely.
        const featureCount = mapPage?.state?.geoData?.features?.length ?? 0;
        if (featureCount < 200 && mapPage) {
          mapPage.loadGeo("cd", null, { doFit: false });
        }
        fired = true;
        clearInterval(poll);
        clearTimeout(fallback);
        setMapReady(true);
        const stepName = STEPS[activeIndexRef.current]?.name;
        if (stepName) {
          navigateToStep(inst, stepName);
        }
      }
    }, 150);
    const fallback = setTimeout(() => setMapReady(true), 30000);
    return () => { clearInterval(poll); clearTimeout(fallback); };
  }, []);

  const handleGeoLevelToggle = useCallback((level) => {
    setGeoLevel(level);
    if (mainRef.current) {
      const step = STEPS[activeIndex];
      mainRef.current.setHighlightsState({
        geoLevel: level,
        preferenceFilter: step?.mapView?.preferenceFilter,
        viewMode: step?.mapView?.viewMode,
        heatDomain: step?.mapView?.heatDomain,
        topStrengthShading: step?.mapView?.topStrengthShading,
      });
    }
  }, [activeIndex]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const container = scrollContainerRef.current;
        const locked = !!container?._scrollLocked;

        if (window.innerWidth < 600) {
          // Membership must stay current even while locked: discarding exit
          // entries during a programmatic scroll left ghost indices that
          // Math.max later resurrected (wrong step, wrong strip, wrong counter).
          entries.forEach((entry) => {
            const idx = Number(entry.target.dataset.index);
            if (entry.isIntersecting) visibleSet.current.add(idx);
            else visibleSet.current.delete(idx);
          });
          if (!locked && visibleSet.current.size > 0) setActiveIndex(Math.max(...visibleSet.current));
        } else {
          if (locked) return;
          const nearestIdx = getNearestCardIndex();
          if (nearestIdx != null) setActiveIndex(nearestIdx);
        }
      },
      { root: mobileMode ? null : scrollContainerRef.current, rootMargin: "0px 0px -40% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    cardRefs.current.forEach((card) => { if (card) observer.observe(card); });
    return () => observer.disconnect();
  }, [getNearestCardIndex, mobileMode]);

  useEffect(() => { activeIndexRef.current = activeIndex; }, [activeIndex]);

  // Desktop resize re-anchor: cards are viewport-sized but scrollTop is pixels,
  // so growing the window parks the column mid-card (and the next wheel flick
  // then looks like it skips two screens). Re-align the active card whenever
  // the window settles at a new size; the map resizes with it.
  useEffect(() => {
    let t;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        if (window.innerWidth < 600) return; // the mobile strip has its own refit
        const container = scrollContainerRef.current;
        const el = cardRefs.current[activeIndexRef.current];
        if (container && el && !container._scrollLocked) {
          el.scrollIntoView({ behavior: "auto", block: "start" });
        }
        getGlMap(mainRef.current)?.resize();
      }, 180);
    };
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); clearTimeout(t); };
  }, []);


  const resizeAndRefit = useCallback(() => {
    let tries = 0;
    const attempt = () => {
      const map = getGlMap(mainRef.current);
      if (!map) {
        if (tries++ < 20) setTimeout(attempt, 100);
        return;
      }
      map.resize();
      const step = STEPS[activeIndexRef.current];
      if (step?.flyTo) mainRef.current?.flyTo?.(flyForViewport(step.flyTo));
    };
    attempt();
  }, []);

  useEffect(() => {
    // Gate on live mobileMode (not a mount-time width read) so rotating into
    // or out of phone width attaches/detaches the strip observer correctly.
    if (!mobileMode) return;
    const raf = requestAnimationFrame(resizeAndRefit);
    const settle = setTimeout(resizeAndRefit, 350);
    let debounce;
    let ro;
    const el = mapPanelRef.current;
    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        clearTimeout(debounce);
        debounce = setTimeout(resizeAndRefit, 150);
      });
      ro.observe(el);
    }
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      clearTimeout(debounce);
      if (ro) ro.disconnect();
    };
  }, [mobileMode, resizeAndRefit]);

  useEffect(() => {
    if (window.innerWidth >= 600) return;
    resizeAndRefit();
    const t = setTimeout(resizeAndRefit, 350);
    return () => clearTimeout(t);
  }, [mapFullscreen, resizeAndRefit]);

  const progress = (activeIndex / (STEPS.length - 1)) * 100;
  const isBackdropBlurStep = activeStep?.name === "intro" || activeStep?.name === "close" || activeStep?.name === "bipartisan";

  return (
    <Box sx={{ bgcolor: BG, height: { xs: "auto", sm: "100vh" }, overflow: { xs: "visible", sm: "hidden" } }}>

      <Box role="progressbar" aria-valuenow={Math.round(progress)} sx={{ position: "fixed", top: 0, left: 0, width: "100%", height: "3px", bgcolor: `${TEAL}18`, zIndex: 1200 }}>
        <Box sx={{ height: "100%", width: `${progress}%`, background: `linear-gradient(90deg, ${TEAL} 0%, ${ACCENT} 100%)`, transition: "width 0.4s ease" }} />
      </Box>

      {activeStep?.name === "close" && (
        <Box
          onClick={() => exitHighlights(navigate)}
          sx={{
          position: "fixed",
          top: { xs: 12, sm: 18 },
          right: { xs: 12, sm: 18 },
          zIndex: 1200,
          width: 36,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          cursor: "pointer",
          color: "rgba(1,2,3,0.46)",
          bgcolor: "rgba(255,255,255,0.8)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(1,2,3,0.08)",
          boxShadow: "0 8px 26px rgba(1,2,3,0.08)",
          transition: "all 0.18s ease",
          "&:hover": { color: NJI_BLACK, borderColor: "rgba(1,2,3,0.14)", bgcolor: "rgba(255,255,255,0.92)" },
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </Box>
      )}

      {/* Mobile: fixed MOBILE_STRIP_H map strip at the top (like PredictionMarketsPage),
          expandable to fullscreen via the zoom toggle. Desktop: right-half panel. */}
      <Box ref={mapPanelRef} sx={{
        display: { xs: hideMapOnXs ? "none" : "block", sm: "block" },
        position: "fixed",
        top: 0,
        left: { xs: 0, sm: isMapBackdropStep ? 0 : "auto" },
        right: 0,
        width: mapWidth,
        height: { xs: mapFullscreen ? "100dvh" : MOBILE_STRIP_H, sm: "100vh" },
        zIndex: { xs: mapFullscreen ? 1300 : 5, sm: 5 },
        overflow: "hidden",
        borderBottom: { xs: "0.5px solid rgba(15,23,42,0.08)", sm: "none" },
        borderLeft: { sm: isMapBackdropStep ? "none" : "0.5px solid rgba(15,23,42,0.06)" },
        bgcolor: "background.paper",
        // Phones: hide the compact Mapbox attribution circle inside the strip
        // (it collided with the expand button; hidden at the client's request).
        "@media (max-width: 599.95px)": {
          "& .mapboxgl-ctrl-attrib": { display: "none" },
        },
        transition: "width 0.75s cubic-bezier(0.22,1,0.36,1), height 0.55s cubic-bezier(0.22,1,0.36,1), top 0.55s cubic-bezier(0.22,1,0.36,1), left 0.75s cubic-bezier(0.22,1,0.36,1)",
      }}>
        <Box sx={{ height: "100%", position: "relative", overflow: "hidden" }}>
          <Box sx={{ height: "100%", filter: isBackdropBlurStep ? "blur(10px) saturate(0.8)" : "none", transform: isBackdropBlurStep ? "scale(1.04)" : "none", transition: "filter 0.75s cubic-bezier(0.22,1,0.36,1), transform 0.75s cubic-bezier(0.22,1,0.36,1)" }}>
            <AppPanelWrapper ref={mainRef} interactive={mapFullscreen} />
          </Box>

          {activeStep?.dimMap && !isIntroStep && (
            <Box sx={{ position: "absolute", inset: 0, zIndex: 3, bgcolor: "rgba(255,255,255,0.42)", backdropFilter: "blur(2px)", pointerEvents: "none", transition: "opacity 0.45s ease" }} />
          )}

          <Box sx={{ position: "absolute", inset: 0, zIndex: 8, cursor: "default", pointerEvents: isBackdropBlurStep ? "auto" : "none" }} />

          <Box sx={{ position: "absolute", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#ffffff", opacity: mapReady ? 0 : 1, pointerEvents: mapReady ? "none" : "auto", transition: "opacity 0.6s ease" }}>
            <CircularProgress size={28} thickness={3.5} sx={{ color: TEAL }} />
          </Box>

          <MapQuestionLegend config={mapFooterConfig} />

          {activeStep?.name === "trust-drivers" && (
            <>
              <MapAgeBadge age={trustDriverAge} />
              <DistrictPopover data={districtPopover} />
            </>
          )}

          {activeStep?.name === "messengers" && (
            <>
              <DistrictPopover data={MESSENGER_STAGE_OVERLAY[messengerStage]} />
              <IntroGeoToggle
                current={messengerGeo}
                onChange={setMessengerGeo}
                levels={[{ key: "states", label: "State" }, { key: "cd", label: "Congressional district", xsLabel: "District" }]}
              />
            </>
          )}

          {activeStep?.showIntroGeoToggle && (
            <IntroGeoToggle current={geoLevel} onChange={handleGeoLevelToggle} />
          )}

          <Box
            onClick={() => setMapFullscreen((f) => !f)}
            sx={{ display: { xs: "flex", sm: "none" }, position: "absolute", bottom: mapFullscreen ? "calc(12px + env(safe-area-inset-bottom))" : 12, right: 12, zIndex: 20, width: 36, height: 36, borderRadius: "10px", bgcolor: mapFullscreen ? NJI_BLUE : "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)", border: mapFullscreen ? `1px solid ${NJI_BLUE}` : `1px solid ${NJI_BLUE}33`, boxShadow: mapFullscreen ? "0 2px 14px rgba(15,110,252,0.4)" : "0 2px 12px rgba(0,0,0,0.08)", alignItems: "center", justifyContent: "center", cursor: "pointer", userSelect: "none", WebkitTapHighlightColor: "transparent" }}
          >
            {mapFullscreen ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "center", gap: 1, position: "fixed", left: 14, top: "50%", transform: "translateY(-50%)", zIndex: 1050 }}>
        {STEPS.map((s, i) => (
          <Box key={i} onClick={() => scrollToIndex(i)} title={s.headline?.slice(0, 48)} sx={{ width: activeIndex === i ? 8 : 5, height: activeIndex === i ? 8 : 5, borderRadius: "50%", bgcolor: activeIndex === i ? NJI_BLUE : `${NJI_BLUE}30`, transition: "all 0.25s ease", cursor: "pointer", "&:hover": { bgcolor: NJI_BLUE, transform: "scale(1.35)" } }} />
        ))}
      </Box>
{/* 
      <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "center", gap: 0.5, position: "fixed", bottom: 0, left: 0, width: { sm: isIntroStep ? "100%" : "53%", md: isIntroStep ? "100%" : "50%" }, pb: 2, pt: 5, background: isIntroStep ? "linear-gradient(to bottom, transparent, rgba(255,255,255,0.18) 55%)" : "linear-gradient(to bottom, transparent, rgba(250,250,252,0.96) 55%)", zIndex: 1040, pointerEvents: "none", opacity: activeIndex === 0 ? 1 : 0, transition: "opacity 0.5s ease, width 0.45s ease" }}>
        <Box sx={{ animation: "nudgeDown 1.6s ease-in-out infinite", "@keyframes nudgeDown": { "0%,100%": { transform: "translateY(0)", opacity: 0.5 }, "50%": { transform: "translateY(5px)", opacity: 1 } } }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </Box>
      </Box> */}

      {activeIndex === 0 && (
        <Box sx={{ display: { xs: "flex", sm: "none" }, position: "fixed", bottom: "calc(48px + env(safe-area-inset-bottom) + 10px)", left: "50%", transform: "translateX(-50%)", zIndex: 1040, alignItems: "center", gap: 0.75, px: 1.75, py: 0.6, borderRadius: "999px", bgcolor: "rgba(255,255,255,0.96)", backdropFilter: "blur(8px)", border: `1px solid ${NJI_BLUE}22`, boxShadow: "0 2px 12px rgba(0,0,0,0.10)", pointerEvents: "none" }}>
          <Typography sx={{ fontSize: { xs: "11.5px", sm: "10px" }, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: NJI_BLUE, fontFamily: F }}>Scroll</Typography>
          <Box sx={{ display: "flex", animation: "nudgeDown 1.6s ease-in-out infinite", "@keyframes nudgeDown": { "0%,100%": { transform: "translateY(0)", opacity: 0.6 }, "50%": { transform: "translateY(3px)", opacity: 1 } } }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={NJI_BLUE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </Box>
        </Box>
      )}

      {/* Mobile nav bar: 48px tall with full-height, generously padded tap
          zones (thumb-friendly), sitting above the iOS home indicator. */}
      <Box sx={{ display: { xs: "flex", sm: "none" }, position: "fixed", bottom: 0, left: 0, right: 0, height: "calc(48px + env(safe-area-inset-bottom))", pb: "env(safe-area-inset-bottom)", bgcolor: "rgba(255,255,255,0.95)", backdropFilter: "blur(10px)", borderTop: "0.5px solid rgba(15,23,42,0.08)", zIndex: 1050, alignItems: "stretch", justifyContent: "space-between" }}>
        <Box onClick={() => { if (activeIndex > 0) scrollToIndex(activeIndex - 1); }} sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 2.5, cursor: activeIndex === 0 ? "default" : "pointer", opacity: activeIndex === 0 ? 0.25 : 1, WebkitTapHighlightColor: "transparent" }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={NJI_BLUE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 6 9 12 15 18"/></svg>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: NJI_BLACK, fontFamily: F }}>Prev</Typography>
        </Box>
        <Typography sx={{ alignSelf: "center", fontSize: { xs: "13px", sm: "11px" }, fontWeight: 600, color: MUTED, fontFamily: F, letterSpacing: "0.05em" }}>{activeIndex + 1} / {STEPS.length}</Typography>
        <Box onClick={() => { if (activeIndex < STEPS.length - 1) scrollToIndex(activeIndex + 1); }} sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 2.5, cursor: activeIndex === STEPS.length - 1 ? "default" : "pointer", opacity: activeIndex === STEPS.length - 1 ? 0.25 : 1, WebkitTapHighlightColor: "transparent" }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: NJI_BLACK, fontFamily: F }}>Next</Typography>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={NJI_BLUE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18"/></svg>
        </Box>
      </Box>

      {/* xs: pt is a CONSTANT (MOBILE_STRIP_H) (the intro card pulls itself up with a
          matching negative margin) so document layout never shifts when the
          active step changes — an animated pt flip moved the whole page a strip-height
          mid-scroll and broke Next/Prev anchoring across the intro boundary. */}
      <Box ref={scrollContainerRef} sx={{ mr: cardMr, pt: { xs: MOBILE_STRIP_H, sm: 0 }, pb: { xs: "calc(68px + env(safe-area-inset-bottom))", sm: 0 }, height: { xs: "auto", sm: "100vh" }, overflowY: { xs: "visible", sm: "scroll" }, scrollSnapType: { xs: "none", sm: "y mandatory" }, "&::-webkit-scrollbar": { display: "none" }, msOverflowStyle: "none", scrollbarWidth: "none", transition: "margin-right 0.75s cubic-bezier(0.22,1,0.36,1)" }}>
        {STEPS.map((step, i) => (
          <NarrativeCard
            key={step.name}
            step={step}
            index={i}
            total={STEPS.length}
            isActive={activeIndex === i}
            cardRef={(el) => { cardRefs.current[i] = el; }}
            mainRef={mainRef}
            onScrollNext={() => scrollToIndex(Math.min(STEPS.length - 1, i + 1))}
            onScrollPrev={() => scrollToIndex(Math.max(0, i - 1))}
            onPanelWheel={handleRightPanelWheel}
            onDistrictPopover={setDistrictPopover}
            trustDriverAge={trustDriverAge}
            setTrustDriverAge={setTrustDriverAge}
            messengerStage={messengerStage}
            setMessengerStage={setMessengerStage}
            messengerGeo={messengerGeo}
            setMessengerGeo={setMessengerGeo}
            survey={survey}
            setSurvey={setSurvey}
          />
        ))}
      </Box>
    </Box>
  );
}
